import React, { useCallback, useEffect, useState } from 'react';
import MobileNav from './MobileNav';
import DesktopSidebar from './DesktopSidebar';
import DesktopHeader from './DesktopHeader';
import './Chrome.css';

// localStorage key for the desktop-sidebar collapsed preference.
const COLLAPSE_KEY = 'sidebarCollapsed';

function readCollapsedInitial() {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Chrome — responsive wrapper around the chat region.
 *
 * The layout switch is driven by CSS @media (min-width: 900px) — no JS
 * viewport listener, no flash-of-wrong-layout on first paint. We render
 * the sidebar, mobile nav, header, and a single <main> slot every time;
 * CSS toggles which of the three chrome pieces are visible.
 *
 * `children` is mounted exactly once (Firestore subscriptions inside the
 * chat region must not double-mount).
 *
 *  < 900px visible parts:  [MobileNav] + <main>{children}</main>
 *  ≥ 900px visible parts:  <DesktopSidebar /> <main><DesktopHeader />{children}</main>
 *
 * Handler props are forwarded to the relevant subcomponent so the parent
 * (App.js / Phase 06) can wire modals once.
 */
const Chrome = ({
  children,
  onAvatarClick,
  onPartyClick,
  onThrowClick,
  onJoinClick,
  onUserClick,
  onRerollClick,
  onSelectRoom,
  onMembersClick,
  presenceText,
  presenceSeeds,
}) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readCollapsedInitial);

  // Persist the preference so a reload keeps the sidebar in the same state.
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, sidebarCollapsed ? '1' : '0');
    } catch {
      /* quota / private mode — non-fatal */
    }
  }, [sidebarCollapsed]);

  const toggleSidebar = useCallback(() => setSidebarCollapsed((c) => !c), []);

  return (
    <div
      className={
        'chrome shell' +
        (sidebarCollapsed ? ' chrome--sidebar-collapsed' : '')
      }
    >
      <div className="chrome__sidebar-slot">
        <DesktopSidebar
          onThrowClick={onThrowClick}
          onJoinClick={onJoinClick}
          onUserClick={onUserClick}
          onRerollClick={onRerollClick}
          onSelectRoom={onSelectRoom}
          onCollapse={toggleSidebar}
        />
      </div>
      <main className="chrome__main">
        <div className="chrome__mobile-slot">
          <MobileNav onAvatarClick={onAvatarClick} onPartyClick={onPartyClick} />
        </div>
        <div className="chrome__header-slot">
          <DesktopHeader
            onMembersClick={onMembersClick}
            presenceText={presenceText}
            presenceSeeds={presenceSeeds}
            sidebarCollapsed={sidebarCollapsed}
            onExpandSidebar={toggleSidebar}
          />
        </div>
        {children}
      </main>
    </div>
  );
};

export default Chrome;
